/**
 * Budget Builder data contract.
 *
 * Mirrors the econ emitter's `{fc_id}_budgetbuilder_satcurve.json`
 * (econ_bigsix_forecasting's src/budget_builder.R) one-to-one — fetched
 * directly from S3 per forecast and merged client-side (see
 * useBudgetBuilderGroupData). Keep in sync with the emitter's output shape.
 */

/** Which series the curve models. `volume` → CPA efficiency, `revenue` → ROAS. */
export type BudgetOutcome = 'volume' | 'revenue';

export type EfficiencyMetric = 'CPA' | 'ROAS';

/** One fitted point on a saturation curve. `null` where the NLS fit produced NA. */
export interface CurvePoint {
  spend: number;
  /** Modeled outcome at this spend (orders for volume, $ for revenue). */
  volume: number | null;
  volumeLower: number | null;
  volumeUpper: number | null;
  /** CPA ($/outcome) or ROAS (outcome/$) at this spend. */
  efficiency: number | null;
  efficiencyLower: number | null;
  efficiencyUpper: number | null;
}

/** A single fitted curve for one spend-group × KPI × outcome. */
export interface CurveSeries {
  outcome: BudgetOutcome;
  efficiencyMetric: EfficiencyMetric;
  /** Sorted ascending by spend. */
  curve: CurvePoint[];
}

/**
 * A spend-group card: one forecast group × KPI, carrying both outcome curves
 * (volume + revenue) when available so the Featured-Variable toggle can switch
 * between them without refetching.
 *
 * Grain-flexible: the budget builder is downstream of the forecast, which runs
 * at whatever grain Data QA blessed. A group therefore carries only the grain
 * dimensions the forecast actually ran at — `platform` and/or `tactic` are empty
 * strings at coarser (rollup) grains. The UI renders whatever is present.
 */
export interface BudgetBuilderGroup {
  /** Stable key (empty grain segments at coarser grains). */
  id: string;
  channel: string;
  /** Empty when the forecast ran above platform grain. */
  platform: string;
  /** Empty when the forecast ran above tactic grain. */
  tactic: string;
  kpi: string;
  /**
   * Primary budget as a WEEKLY spend — the CP's primary budget (the "100%"
   * scenario the comparison budgets are relative to) divided by the number of
   * weeks in the forecast period. Weekly (not a period total) so it aligns to a
   * point on the weekly curve regardless of forecast length; the card multiplies
   * by the weeks to show the period total. Optional: the "vs primary" reference
   * renders only when the emitter provides it.
   */
  primarySpend?: number;
  series: CurveSeries[];
}

export interface BudgetBuilderData {
  /** Forecast period; used to convert weekly figures to period totals when the page doesn't supply one. */
  period?: { start: string; end: string };
  groups: BudgetBuilderGroup[];
}

export const EMPTY_BUDGET_BUILDER_DATA: BudgetBuilderData = { groups: [] };
